import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { Box, Grid, Paper } from '@mui/material';
import { SCUIContent } from '../../../atomic/organisms';
import { SCUIBreadCrumb, SCUIGeneralDrawer } from '../../../atomic/molecules';
import { SCUIButton, SCUITabs } from '../../../atomic/atoms';
import { AddOrEditSubSectionDrawer } from './components/AddOrEditSubSectionDrawer';
import { AddOrEditParameterDrawer } from './components/AddOrEditParameterDrawer';
import { AddOrEditSectionInfos } from './components/AddOrEditSectionInfos';
import { AddSubSectionNameDrawer } from './components/AddSubSectionNameDrawer';
import Swal from 'sweetalert2';
import MonacoEditor from 'react-monaco-editor';
import 'monaco-editor/esm/vs/basic-languages/html/html.contribution';
import * as monaco from 'monaco-editor';
import { ETBED, etbCls } from '../emailTemplateBuilder.classes';

export const EditorAddOrEdit: FC<any> = ({ editDatas }) => {
    const { t } = useTranslation();
    const [tabIndex, setTabIndex] = useState<any>(1);
    const documentType = t('emailTemplateBuilder.codeEditor');
    const [documentTypeText, setDocumentTypeText] = React.useState(documentType);
    const [editorData, setEditorData] = useState('');
    const [drawerAddOpen, setDrawerAddOpen] = React.useState(false);
    const [drawerSubSectionNameAddOpen, setDrawerSubSectionNameAddOpen] = React.useState(false);

    const [parameterAddDrawerOpen, setParameterAddDrawerOpen] = React.useState(false);
    const [generatedParameterValue, setGeneratedParameterValue] = useState('');
    const [drawerSectionInfosOpen, setDrawerSectionInfosOpen] = useState(false);
    const [sectionInfos, setSectionInfos] = React.useState<any>();

    const [selectedSubSectionList, setSelectedSubSectionList] = React.useState<any>();
    const [editorInstance, setEditorInstance] = useState<any>(null);
    const [monacoInstance, setMonacoInstance] = useState<any>(null);

    const enumNotificationTabsIndexTabs = [
        { id: 1, name: t('emailTemplateBuilder.codeEditor') },
        { id: 2, name: t('emailTemplateBuilder.preview') },
    ];
    const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
    const _onChangeTabs = (event: any, activeTabIndex: any): void => {
        setDocumentTypeText(event.target.innerText);
        setTabIndex(activeTabIndex);
    };

    useEffect(() => {
        if (editDatas) {
            const prettyHtml = editDatas.content.replace(/></g, '>\n<');
            setEditorData(prettyHtml);
            setSectionInfos(editDatas);
        }
    }, []);

    const editorDidMount = (editor: any, monaco: any) => {
        setEditorInstance(editor);
        setMonacoInstance(monaco);
    };

    const editoronChange = (editorData: any) => {
        setEditorData(editorData);
    };

    const onParameterAddDrawerClose = () => setParameterAddDrawerOpen(false);
    const onDrawerSectionInfosClose = () => setDrawerSectionInfosOpen(false);
    const onDrawerSubSectionNameAddClose = () => setDrawerSubSectionNameAddOpen(false);

    useEffect(() => {
        if (!editorInstance || !monacoInstance) return;
        const selection = editorInstance.getSelection();
        if (!selection) return;

        const range = new monacoInstance.Range(
            selection.startLineNumber,
            selection.startColumn,
            selection.startLineNumber,
            selection.startColumn
        );

        editorInstance.executeEdits('', [{ range: range, text: selectedSubSectionList, forceMoveMarkers: true }]);
    }, [selectedSubSectionList, editorInstance, monacoInstance]);

    useEffect(() => {
        if (!editorInstance || !monacoInstance) return;
        const selection = editorInstance.getSelection();
        if (!selection) return;
        const range = new monacoInstance.Range(
            selection.startLineNumber,
            selection.startColumn,
            selection.endLineNumber,
            selection.endColumn
        );
        editorInstance.executeEdits('', [{ range: range, text: generatedParameterValue, forceMoveMarkers: true }]);
    }, [generatedParameterValue, editorInstance, monacoInstance]);

    return (
        <SCUIContent>
            <Box className={ETBED.page}>
            <SCUIGeneralDrawer
                open={drawerAddOpen}
                width={'400px'}
                onClose={(_: any, reason: any) => {
                    if (reason === 'backdropClick') {
                        setDrawerAddOpen(false);
                    }
                }}
            >
                <AddOrEditSubSectionDrawer setSelectedSubSectionList={setSelectedSubSectionList} />
            </SCUIGeneralDrawer>
            <SCUIGeneralDrawer
                open={drawerSubSectionNameAddOpen}
                width={'400px'}
                onClose={(_: any, reason: any) => {
                    if (reason === 'backdropClick') {
                        setDrawerSubSectionNameAddOpen(false);
                    }
                }}
            >
                <AddSubSectionNameDrawer
                    onClose={onDrawerSubSectionNameAddClose}
                    setSelectedSubSectionList={setSelectedSubSectionList}
                />
            </SCUIGeneralDrawer>
            <SCUIGeneralDrawer
                open={parameterAddDrawerOpen}
                width={'400px'}
                onClose={(_: any, reason: any) => {
                    if (reason === 'backdropClick') {
                        setParameterAddDrawerOpen(false);
                    }
                }}
            >
                <AddOrEditParameterDrawer
                    onClose={onParameterAddDrawerClose}
                    setGeneratedParameterValue={setGeneratedParameterValue}
                />
            </SCUIGeneralDrawer>
            <SCUIGeneralDrawer
                open={drawerSectionInfosOpen}
                width={'400px'}
                onClose={(event: any, reason: any) => {
                    if (reason === 'backdropClick') {
                        setDrawerSectionInfosOpen(false);
                    }
                }}
            >
                <AddOrEditSectionInfos
                    onClose={onDrawerSectionInfosClose}
                    sectionInfos={sectionInfos}
                    setSectionInfos={setSectionInfos}
                    editorContent={editorData}
                />
            </SCUIGeneralDrawer>
            <SCUIBreadCrumb
                dontShowHome={true}
                homeIconLink={'/home'}
                onlyTitle={'Email Template Builder'}
                components={[
                    {
                        title: t('emailTemplateBuilder.templateBuilder'),
                        link: '/emailTemplateBuilder',
                    },
                    {
                        title: t('emailTemplateBuilder.sections'),
                        link: '/emailTemplateBuilder/sectionGallery',
                    },
                    !editDatas
                        ? {
                              title: t('emailTemplateBuilder.createNewSection'),
                              link: '/emailTemplateBuilder/addSection',
                          }
                        : {
                              title: t('emailTemplateBuilder.update'),
                              link: '/emailTemplateBuilder/update',
                          },
                ]}
            />
            <div className={etbCls('lineWrapper', ETBED.divider)} />
            <Grid
                className={ETBED.titleRow}
                size={{
                    lg: 10,
                    md: 6,
                    xs: 6,
                }}
            >
                {!editDatas ? (
                    <h1 className="headtxt">{t('emailTemplateBuilder.createNewSection')} </h1>
                ) : (
                    <h1 className="headtxt">{t('emailTemplateBuilder.update')} </h1>
                )}
            </Grid>
            {documentTypeText && documentTypeText == t('emailTemplateBuilder.codeEditor') ? (
                <Grid sx={{ display: 'flex', alignItems: 'flex-end' }}>
                    <Grid
                        className={ETBED.saveToolbarRow}
                        sx={{
                            marginBottom: '1rem',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            marginTop: '16px',
                        }}
                        size={{
                            lg: 10,
                            md: 10,
                            xs: 12,
                        }}
                    >
                        <SCUIButton
                            className={ETBED.inputButtonOpenSectionInfos}
                            text={editDatas ? t('general.update') : t('general.save')}
                            type="primary"
                            sizeType="fixed"
                            size={'s'}
                            onClick={() => setDrawerSectionInfosOpen(true)}
                        />
                    </Grid>
                </Grid>
            ) : (
                <div></div>
            )}
            <Grid className={ETBED.tabsRow} sx={{ marginTop: '1rem' }}>
                <SCUITabs
                    tabList={Object.values(enumNotificationTabsIndexTabs)}
                    onChange={_onChangeTabs}
                    activeTabIndex={tabIndex}
                ></SCUITabs>
            </Grid>
            <Paper className={ETBED.workspacePaper}>
                {documentTypeText == t('emailTemplateBuilder.codeEditor') ? (
                    <Grid container>
                        <Grid
                            className={ETBED.sidebarActions}
                            style={{
                                marginTop: '2rem',
                                display: 'flex',
                                justifyContent: 'flex-start',
                                flexDirection: 'column',
                                gap: '2rem',
                                maxWidth: '200px',
                                marginLeft: '1rem',
                            }}
                            size={{
                                sm: 3,
                                md: 3,
                                lg: 3,
                                xl: 3,
                            }}
                        >
                            <SCUIButton
                                className={ETBED.inputButtonAddSubsectionContent}
                                text={t('emailTemplateBuilder.addSubsectionButtonText')}
                                type="secondary"
                                sizeType="fixed"
                                size={'s'}
                                onClick={() => setDrawerAddOpen(true)}
                            />
                            <SCUIButton
                                className={ETBED.inputButtonAddSubsectionName}
                                text={t('emailTemplateBuilder.addSubsectionName')}
                                type="secondary"
                                sizeType="fixed"
                                size={'s'}
                                onClick={() => {
                                    if ((editorRef.current as any)?.editorInstance.mode !== 'source') {
                                        setDrawerSubSectionNameAddOpen(true);
                                    } else {
                                        Swal.fire('Ekleme yapmak için source modunu kapatınız', ' ', 'error');
                                    }
                                }}
                            />
                            <SCUIButton
                                className={ETBED.inputButtonAddParameter}
                                text={t('emailTemplateBuilder.addParameter')}
                                type="secondary"
                                sizeType="fixed"
                                size={'s'}
                                onClick={() => {
                                    if ((editorRef.current as any)?.editorInstance.mode !== 'source') {
                                        setParameterAddDrawerOpen(true);
                                    } else {
                                        Swal.fire('Ekleme yapmak için source modunu kapatınız', ' ', 'error');
                                    }
                                }}
                            />
                        </Grid>
                        <Grid
                            className={ETBED.editorPane}
                            sx={{ marginLeft: '2rem' }}
                            size={{
                                sm: 9,
                                md: 9,
                                lg: 9,
                                xl: 9,
                            }}
                        >
                            <MonacoEditor
                                height="400"
                                language="html"
                                theme="vs-dark"
                                value={editorData}
                                options={{
                                    selectOnLineNumbers: true,
                                }}
                                editorDidMount={editorDidMount}
                                onChange={editoronChange}
                            />
                        </Grid>
                    </Grid>
                ) : (
                    <Grid
                        className={ETBED.previewPane}
                        sx={{ marginLeft: '2rem' }}
                        size={{
                            sm: 9,
                            md: 9,
                            lg: 9,
                            xl: 9,
                        }}
                    >
                        <div
                            className={ETBED.previewScroll}
                            style={{
                                height: '400px',
                                overflow: 'auto',
                                backgroundColor: 'transparent',
                            }}
                        >
                            {parse(DOMPurify.sanitize(editorData))}
                        </div>
                    </Grid>
                )}
            </Paper>
            </Box>
        </SCUIContent>
    );
};
